/*@flow*/
/** @jsx jsx */
import { jsx, css } from '@emotion/core';
import styled from '@emotion/styled';
import { LEVEL, PAGE_NAV_HEIGHT, BREAKPOINT_MD } from 'store/constants';
import config from './config';
import { type ContextRouter } from 'react-router-dom';
import { withRouter } from 'react-router';
import slug from 'lib/slug';
import { updateScrollPosition } from 'lib/scrollToAnchor';
import { useEffect, useState } from 'react';
import * as I from 'immutable';
import cx from 'classnames';

const Container = styled.nav(`
  position: sticky;
  top: 0px;
  z-index: ${LEVEL.PAGE_NAV};
  background: var(--brand-black);
  margin: 0;
  height: ${PAGE_NAV_HEIGHT.toString()};
  @media (max-width: ${BREAKPOINT_MD.toString()}) {
    width: 102%;
    margin-left: -1px;
    .name, .green-triangle, .black-triangle {
      display: none !important;
    }
    .numbers {
      display: inline-block !important;
    }
  }

  font-size: var(--font-size-h-five);
`);

const Triangle = styled.div(
  props => `
  border-top: ${props.size}px solid transparent;
  border-left: ${props.size}px solid ${props.tColor || 'var(--brand-green)'};
  border-bottom: ${props.size}px solid transparent;
  border-right: ${props.size}px solid transparent;
  background: transparent;
  height: 0;
  width: 0;

  ${props.styles}
`
);

const Button = withRouter(
  (props: {|
    name: string,
    index: number,
    isActive: boolean,
    ...ContextRouter,
  |}) => {
    const anchor = slug(props.name);

    return (
      <button
        css={css(`
          &.active, &:hover{
            background: var(--brand-green);
            color: black;
            .green-triangle, .black-triangle {
              display: block;
            }
            &:first-of-type {
              .black-triangle {
                display: none;
              }
            }
          }
          &.active {
            .black-triangle {
              display: block;
            }
          }

          &:focus { outline: 0; }
          display: grid;
          justify-items: center;
          align-items: center;
          width: 100%;
          height: 100%;
          position: relative;
          .numbers { display: none; }
        `)}
        className={cx({ active: props.isActive })}
        onClick={() => {
          setTimeout(
            () =>
              props.history.push({ ...props.location, query: { t: anchor } }),
            200
          );
          updateScrollPosition(anchor);
        }}
      >
        <span>
          <Triangle
            className="black-triangle"
            tColor="var(--brand-black)"
            size={20}
            styles={`
              position: absolute;
              left: 0px;
              top: 0px;
              display: none;
            `}
          />
          <span className="name">{props.name}</span>
          <span className="numbers">{props.index + 1}</span>{' '}
          <Triangle
            className="green-triangle"
            size={20}
            styles={`
              position: absolute;
              right: -40px;
              top: 0px;
              display: none;
              z-index: 999;
            `}
          />
        </span>
      </button>
    );
  }
);

const Component = () => {
  const names = config.map(v => v.name);
  const firstSectionName = names[0];
  const [activeName, setActiveName] = useState(firstSectionName);
  const [sectionsInView, setSectionsInView] = useState(I.Set());

  const onSectionChange = (e: any) => {
    const { inView, name } = e.detail;
    if (inView) {
      setSectionsInView(v => v.add(name));
    } else {
      setSectionsInView(v => v.delete(name));
    }
  };

  useEffect(() => {
    document.addEventListener('section-change', onSectionChange);
    return () =>
      document.removeEventListener('section-change', onSectionChange);
  }, []);

  useEffect(() => {
    setActiveName(sectionsInView.sortBy(v => names.indexOf(v)).first());
  }, [sectionsInView.hashCode()]);

  return (
    <Container>
      <div
        css={css(`
        height: 100%;
        display: grid;
        grid-template-columns: repeat(${config.length}, 1fr);
        justify-items: center; align-items: center;
      `)}
      >
        {config.map((item, i) => (
          <Button
            name={item.name}
            key={item.name}
            index={i}
            isActive={activeName === item.name}
          />
        ))}
      </div>
    </Container>
  );
};

export default Component;
